/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import {
  FiSearch,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiArrowRight,
  FiSliders,
  FiCamera,
  FiFileText,
} from "react-icons/fi";
import { BRANDS, PRODUCT_DETAIL, PRODUCT_LIST_BUY } from "../../constant";
import { GET, POST } from "../../constant/apiMethods";
import Labels from "../../services/apiLabels";
import config from "../../config";
import { scrollTop } from "../../utils/window";
import {
  resolveVehicleImageUrl,
  sanitizeImageUrl,
} from "../../utils/sanitizeImageUrl";
import { getBuyNowDisplayPrice } from "../../utils/listingPrice";
import VehicleReportModal from "./VehicleReportModal";
import "./cars.css";

const publicUrl = process.env.PUBLIC_URL || "";
const PAGE_SIZE = 12;
const DEFAULT_VEHICLE_IMAGE = `${publicUrl}/images/cars-placeholder.jpg`;
const API_BASE = (config.baseUrl || "").replace(/\/?$/, "/");

const SORT_OPTIONS = [
  { value: "1", label: "Price · low to high" },
  { value: "2", label: "Price · high to low" },
  { value: "3", label: "Mileage · low to high" },
  { value: "4", label: "Mileage · high to low" },
  { value: "5", label: "Make · A–Z" },
  { value: "6", label: "Make · Z–A" },
];

const TRANSMISSION_OPTIONS = [
  { value: "", label: "Any gearbox" },
  { value: "Manual", label: "Manual" },
  { value: "Automatic", label: "Automatic" },
];

const MILEAGE_OPTIONS = [
  { value: "", label: "Any mileage" },
  { value: "1000", label: "Up to 1,000 km" },
  { value: "20000", label: "Up to 20,000 km" },
  { value: "50000", label: "Up to 50,000 km" },
  { value: "100000", label: "Up to 100,000 km" },
  { value: "150000", label: "Up to 150,000 km" },
  { value: "200000", label: "Up to 200,000 km" },
];

function formatPrice(item) {
  if (item?.poa) return "POA";
  const raw = getBuyNowDisplayPrice(item, { buyNowFloor: true });
  if (raw == null || raw === "" || raw === "None" || raw === "N/A") {
    return "Price on request";
  }
  const num = Number(raw);
  if (Number.isNaN(num)) return String(raw);
  return `€${num.toLocaleString()}`;
}

function cleanValue(value) {
  if (value == null || value === "" || value === "None" || value === "N/A") {
    return null;
  }
  return String(value).trim() || null;
}

function displayValue(value, fallback = "Not specified") {
  return cleanValue(value) || fallback;
}

function formatMileage(value) {
  const cleaned = cleanValue(value);
  if (!cleaned) return "Not specified";
  const num = Number(cleaned);
  if (!Number.isNaN(num)) return `${num.toLocaleString()} km`;
  return `${cleaned} km`;
}

function matchesSearch(item, query) {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    item?.product_name,
    item?.brands,
    item?.model_name,
    item?.fuel_type,
    item?.year,
    item?.transmission,
    item?.mileage,
    item?.product_description,
  ]
    .filter((v) => cleanValue(v))
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
}

function getProductId(item) {
  return item?.id ?? item?.product_id ?? item?.productId ?? null;
}

function hasReportHint(item) {
  if (!item || typeof item !== "object") return false;
  if (item.has_report === true || item.report_available === true) return true;
  if (Number(item.total_inspect) > 0) {
    if (Array.isArray(item.report?.data) && item.report.data.length > 0) {
      return true;
    }
    if (Array.isArray(item.inspection_sections) && item.inspection_sections.length) {
      return true;
    }
  }
  if (item.report && typeof item.report === "object") {
    return isCompletedInspectionReport(item.report);
  }
  return false;
}

function isCompletedInspectionReport(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return false;
  }
  const score = Number(payload.total_inspect);
  const sections = Array.isArray(payload.data) ? payload.data : [];
  // Final report exists only after inspection is completed and published.
  return score > 0 && sections.length > 0;
}

async function checkReportAvailable(productId) {
  if (productId == null || productId === "") return false;
  try {
    const response = await fetch(`${API_BASE}${Labels.car_report_detail}`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ id: productId }),
    });
    if (!response.ok) return false;
    const json = await response.json();
    return isCompletedInspectionReport(json?.data);
  } catch (error) {
    return false;
  }
}

function transmissionLabel(value) {
  if (value === 1 || value === "1") return "Automatic";
  if (value === 2 || value === "2") return "Manual";
  if (value === 3 || value === "3") return "Both";
  return cleanValue(value);
}

function bodyTypeLabel(value) {
  if (value === 1 || value === "1") return "Hatchback";
  if (value === 2 || value === "2") return "MPV";
  if (value === 3 || value === "3") return "SUV";
  if (value === 4 || value === "4") return "Saloon";
  return cleanValue(value);
}

function unwrapProductPayload(product) {
  if (!product || typeof product !== "object" || Array.isArray(product)) {
    return {};
  }
  // saga stores response.data — usually the vehicle object; tolerate nested wraps
  if (
    product.data &&
    typeof product.data === "object" &&
    !Array.isArray(product.data) &&
    (product.data.id != null || product.data.product_name) &&
    !product.product_name
  ) {
    return product.data;
  }
  return product;
}

function collectGallerySlides(item) {
  if (!item) return [];

  const brand = cleanValue(item?.brands);
  const model = cleanValue(item?.model_name);
  const productName = cleanValue(item?.product_name);
  const vehicleLabel =
    [brand, model].filter(Boolean).join(" ") ||
    productName ||
    "Vehicle";

  const slides = [];
  const seen = new Set();

  const pushSlide = (url, caption) => {
    const clean = sanitizeImageUrl(url);
    if (!clean || /notavailable\.jpg/i.test(clean) || seen.has(clean)) return;
    seen.add(clean);
    slides.push({ url: clean, caption });
  };

  if (item.main_image) {
    pushSlide(item.main_image, `${vehicleLabel} · Main photo`);
  }
  if (item.product_image) {
    pushSlide(item.product_image, `${vehicleLabel} · Product photo`);
  }
  if (item.image) {
    pushSlide(item.image, `${vehicleLabel} · Listing photo`);
  }
  if (Array.isArray(item.images)) {
    item.images.forEach((url, index) => {
      pushSlide(url, `${vehicleLabel} · Photo ${index + 1}`);
    });
  }

  const inspection = item.images_inspection;
  if (inspection && typeof inspection === "object") {
    const groups = [
      ["exterior_images_", "Exterior"],
      ["interior_images_", "Interior"],
      ["wheels_images_", "Wheels"],
      ["treads_images_", "Tyre treads"],
    ];
    groups.forEach(([key, label]) => {
      if (!Array.isArray(inspection[key])) return;
      inspection[key].forEach((url, index) => {
        pushSlide(url, `${vehicleLabel} · ${label} ${index + 1}`);
      });
    });
  }

  if (!slides.length) {
    slides.push({
      url: DEFAULT_VEHICLE_IMAGE,
      caption: `${vehicleLabel} · Photo unavailable`,
    });
  }

  return slides;
}

function collectNamedFeatureList(list) {
  if (!Array.isArray(list)) return [];
  return list.filter((f) => cleanValue(f?.name) || cleanValue(f?.value));
}

function formatFeatureLabel(item) {
  const name = cleanValue(item?.name);
  const value = cleanValue(item?.value);
  if (name && value && name.toLowerCase() !== value.toLowerCase()) {
    return `${name} · ${value}`;
  }
  return name || value || "";
}

const DETAIL_FALLBACK = "—";

function formatVat(value) {
  const cleaned = cleanValue(value);
  if (!cleaned) return DETAIL_FALLBACK;
  const num = Number(cleaned);
  if (!Number.isNaN(num)) return `${num}%`;
  return cleaned;
}

function formatMoney(value, fallback = DETAIL_FALLBACK) {
  const cleaned = cleanValue(value);
  if (!cleaned) return fallback;
  const num = Number(cleaned);
  if (Number.isNaN(num)) return cleaned;
  return `€${num.toLocaleString()}`;
}

function formatEndAt(value) {
  const cleaned = cleanValue(value);
  if (!cleaned) return DETAIL_FALLBACK;
  const date = new Date(cleaned);
  if (Number.isNaN(date.getTime())) return cleaned;
  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function mergeProductDetail(apiProduct, listItem) {
  const api = unwrapProductPayload(apiProduct);
  const list = listItem && typeof listItem === "object" ? listItem : {};
  const listId = getProductId(list);
  const apiId = getProductId(api);
  const apiMatches =
    apiId != null && (listId == null || String(listId) === String(apiId));
  const hasApi =
    apiMatches &&
    (Boolean(api.id || api.product_name || api.Registration) ||
      (Array.isArray(api.images) && api.images.length > 0) ||
      Boolean(api.main_image));

  const source = hasApi ? { ...list, ...api } : { ...list };

  return {
    ...source,
    id: source.id ?? list.id ?? null,
    product_name: source.product_name ?? list.product_name ?? null,
    model_name: source.model_name ?? list.model_name ?? null,
    brands: source.brands ?? list.brands ?? null,
    brands_image: source.brands_image || list.brands_image || null,
    mileage: source.Mileage ?? source.mileage ?? list.mileage ?? null,
    transmission:
      transmissionLabel(source.Transmission) ||
      cleanValue(source.transmission) ||
      cleanValue(list.transmission) ||
      null,
    body_type:
      bodyTypeLabel(source.car_body_type) ||
      cleanValue(list.body_type) ||
      null,
    registration:
      cleanValue(source.Registration) ||
      cleanValue(source.registration) ||
      cleanValue(list.registration) ||
      null,
    post_code:
      cleanValue(source.post_code) || cleanValue(list.post_code) || null,
    vat: source.vat ?? list.vat ?? null,
    fuel_type: source.fuel_type ?? list.fuel_type ?? null,
    year: source.year ?? list.year ?? null,
    auction_id: cleanValue(source.auction_id) || null,
    product_category: cleanValue(source.product_category) || null,
    product_price: source.product_price ?? list.product_price ?? null,
    buy_now_price:
      source.buy_now_price ?? list.buy_now_price ?? null,
    buy_now: source.buy_now ?? list.buy_now ?? null,
    price:
      getBuyNowDisplayPrice(
        {
          ...list,
          ...source,
          buy_now: source.buy_now ?? list.buy_now,
          buy_now_price: source.buy_now_price ?? list.buy_now_price,
          price: source.price ?? list.price,
          product_price: source.product_price ?? list.product_price,
        },
        { buyNowFloor: true }
      ) ??
      source.buy_now_price ??
      list.buy_now_price ??
      source.product_price ??
      list.product_price ??
      source.price ??
      list.price ??
      null,
    start_price: source.start_price ?? null,
    increment_value: source.increment_value ?? null,
    end_at: source.end_at ?? null,
    end_at_milli: source.end_at_milli ?? null,
    product_description:
      source.product_description ?? list.product_description ?? null,
    images_inspection: source.images_inspection || {
      exterior_images_: [],
      interior_images_: [],
      wheels_images_: [],
      treads_images_: [],
    },
    product_feature: Array.isArray(source.product_feature)
      ? source.product_feature
      : Array.isArray(list.product_feature)
        ? list.product_feature
        : [],
    product_accessory: Array.isArray(source.product_accessory)
      ? source.product_accessory
      : Array.isArray(list.product_accessory)
        ? list.product_accessory
        : [],
    bids_detail: Array.isArray(source.bids_detail) ? source.bids_detail : [],
    images: Array.isArray(source.images)
      ? source.images
      : Array.isArray(list.images)
        ? list.images
        : [],
  };
}

function valOrFallback(value, formatter) {
  if (formatter) return formatter(value);
  return cleanValue(value) || DETAIL_FALLBACK;
}

function SpecRows({ rows }) {
  return (
    <dl className="pi-cars-modal__rows">
      {rows.map((row) => {
        const missing = row.value === DETAIL_FALLBACK;
        return (
          <div
            key={row.label}
            className={`pi-cars-modal__row${missing ? " is-missing" : ""}`}
          >
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </div>
        );
      })}
    </dl>
  );
}

function ChipBlock({ items }) {
  if (!items.length) {
    return <p className="pi-cars-modal__empty-line">{DETAIL_FALLBACK}</p>;
  }
  return (
    <ul className="pi-cars-modal__chips">
      {items.map((item, index) => (
        <li key={`${item?.name}-${item?.value}-${index}`}>
          {formatFeatureLabel(item)}
        </li>
      ))}
    </ul>
  );
}

function VehicleDetailModal({
  open,
  listItem,
  product,
  loading,
  onClose,
  reportAvailable,
  onReport,
}) {
  const detail = useMemo(
    () => mergeProductDetail(product, listItem),
    [product, listItem]
  );

  const features = collectNamedFeatureList(detail?.product_feature);
  const accessories = collectNamedFeatureList(detail?.product_accessory);
  const title = displayValue(detail?.product_name, "Vehicle details");
  const model = cleanValue(detail?.model_name) || DETAIL_FALLBACK;
  const brandName = cleanValue(detail?.brands);
  const description =
    cleanValue(detail?.product_description) || DETAIL_FALLBACK;
  const registration = cleanValue(detail?.registration) || DETAIL_FALLBACK;
  const askingPrice = detail?.poa
    ? "POA"
    : formatMoney(
        getBuyNowDisplayPrice(detail, { buyNowFloor: true }) ??
          detail?.buy_now_price ??
          detail?.product_price ??
          detail?.price,
        "Price on request"
      );

  const highlightFacts = [
    {
      label: "Year",
      value: valOrFallback(detail?.year),
    },
    {
      label: "Fuel",
      value: valOrFallback(detail?.fuel_type),
    },
    {
      label: "Gearbox",
      value: valOrFallback(detail?.transmission),
    },
    {
      label: "Mileage",
      value: cleanValue(detail?.mileage)
        ? formatMileage(detail.mileage)
        : DETAIL_FALLBACK,
    },
  ];

  const vehicleRows = [
    { label: "Product ID", value: valOrFallback(detail?.id) },
    { label: "Model", value: valOrFallback(detail?.model_name) },
    { label: "Body type", value: valOrFallback(detail?.body_type) },
    { label: "Registration", value: valOrFallback(detail?.registration) },
    { label: "Postcode", value: valOrFallback(detail?.post_code) },
    { label: "Category", value: valOrFallback(detail?.product_category) },
  ];

  const listingRows = [
    { label: "Auction ID", value: valOrFallback(detail?.auction_id) },
    { label: "VAT", value: formatVat(detail?.vat) },
    { label: "Start price", value: formatMoney(detail?.start_price) },
    { label: "Increment", value: formatMoney(detail?.increment_value) },
    { label: "Ends at", value: formatEndAt(detail?.end_at) },
    {
      label: "Bids",
      value: Array.isArray(detail?.bids_detail)
        ? String(detail.bids_detail.length)
        : "0",
    },
  ];

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="pi-cars-modal" role="presentation" onClick={onClose}>
      <div
        className="pi-cars-modal__dialog pi-cars-modal__dialog--details"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pi-cars-detail-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="pi-cars-modal__close"
          onClick={onClose}
          aria-label="Close details"
        >
          <FiX size={18} aria-hidden />
        </button>

        <div className="pi-cars-modal__header">
          <p className="pi-cars-modal__eyebrow">
            {brandName ? `${brandName} · ${model}` : model}
          </p>
          <h2 id="pi-cars-detail-title">{title}</h2>
          {registration && registration !== DETAIL_FALLBACK ? (
            <p className="pi-cars-modal__reg">
              Registration <strong>{String(registration).toUpperCase()}</strong>
            </p>
          ) : null}
        </div>

        <div className="pi-cars-modal__body">
          {loading ? (
            <div className="pi-cars-modal__loading" aria-live="polite">
              <span className="pi-cars-modal__spinner" aria-hidden />
              Loading details…
            </div>
          ) : null}

          <div className="pi-cars-modal__pricebar">
            <div>
              <span>Product price</span>
              <strong>{askingPrice}</strong>
            </div>
            <div>
              <span>Start price</span>
              <strong>{formatMoney(detail?.start_price)}</strong>
            </div>
          </div>

          {reportAvailable ? (
            <div className="pi-cars-modal__report">
              <div>
                <strong>Final inspection report</strong>
                <span>
                  Available after registration and completed inspection.
                </span>
              </div>
              <button
                type="button"
                className="pi-cars-modal__report-btn"
                onClick={() => onReport?.(listItem || detail)}
              >
                <FiFileText size={16} aria-hidden />
                View report
              </button>
            </div>
          ) : null}

          <div className="pi-cars-modal__facts">
            {highlightFacts.map((fact) => (
              <div
                key={fact.label}
                className={`pi-cars-modal__fact${
                  fact.value === DETAIL_FALLBACK ? " is-missing" : ""
                }`}
              >
                <span>{fact.label}</span>
                <strong>{fact.value}</strong>
              </div>
            ))}
          </div>

          <div className="pi-cars-modal__panels">
            <section className="pi-cars-modal__panel">
              <h3>Vehicle</h3>
              <SpecRows rows={vehicleRows} />
            </section>
            <section className="pi-cars-modal__panel">
              <h3>Listing</h3>
              <SpecRows rows={listingRows} />
            </section>
          </div>

          <section className="pi-cars-modal__panel">
            <h3>Description</h3>
            <p
              className={
                description === DETAIL_FALLBACK
                  ? "pi-cars-modal__empty-line"
                  : "pi-cars-modal__copy"
              }
            >
              {description}
            </p>
          </section>

          <div className="pi-cars-modal__panels">
            <section className="pi-cars-modal__panel">
              <h3>Features</h3>
              <ChipBlock items={features} />
            </section>
            <section className="pi-cars-modal__panel">
              <h3>Accessories</h3>
              <ChipBlock items={accessories} />
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

function VehicleGalleryModal({ open, item, onClose }) {
  const slides = useMemo(() => collectGallerySlides(item), [item]);
  const [index, setIndex] = useState(0);
  const [imageSrc, setImageSrc] = useState(DEFAULT_VEHICLE_IMAGE);
  const thumbsRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setIndex(0);
    const onKey = (event) => {
      if (event.key === "Escape") onClose?.();
      if (event.key === "ArrowRight") {
        setIndex((current) => (current + 1) % Math.max(slides.length, 1));
      }
      if (event.key === "ArrowLeft") {
        setIndex((current) =>
          (current - 1 + Math.max(slides.length, 1)) % Math.max(slides.length, 1)
        );
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, slides.length]);

  useEffect(() => {
    const next = slides[index]?.url || DEFAULT_VEHICLE_IMAGE;
    setImageSrc(next);
  }, [slides, index]);

  useEffect(() => {
    if (!open || !thumbsRef.current) return;
    const active = thumbsRef.current.querySelector("[data-active='true']");
    if (active && typeof active.scrollIntoView === "function") {
      active.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [open, index]);

  if (!open || !item) return null;

  const slide = slides[index] || slides[0];
  const caption = slide?.caption || "Vehicle photo";
  const total = slides.length;
  const canNavigate = total > 1;

  return (
    <div
      className="pi-cars-gallery"
      role="dialog"
      aria-modal="true"
      aria-label="Vehicle photos"
      onClick={onClose}
    >
      <div
        className="pi-cars-gallery__dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="pi-cars-gallery__close"
          onClick={onClose}
          aria-label="Close gallery"
        >
          <FiX size={22} aria-hidden />
        </button>

        <div className="pi-cars-gallery__panel">
          <div className="pi-cars-gallery__stage">
            {canNavigate ? (
              <button
                type="button"
                className="pi-cars-gallery__nav pi-cars-gallery__nav--prev"
                onClick={() =>
                  setIndex((current) => (current - 1 + total) % total)
                }
                aria-label="Previous photo"
              >
                <FiChevronLeft size={24} aria-hidden />
              </button>
            ) : null}

            <img
              src={imageSrc}
              alt={caption}
              className="pi-cars-gallery__image"
              onError={() => {
                if (imageSrc !== DEFAULT_VEHICLE_IMAGE) {
                  setImageSrc(DEFAULT_VEHICLE_IMAGE);
                }
              }}
            />

            {canNavigate ? (
              <button
                type="button"
                className="pi-cars-gallery__nav pi-cars-gallery__nav--next"
                onClick={() => setIndex((current) => (current + 1) % total)}
                aria-label="Next photo"
              >
                <FiChevronRight size={24} aria-hidden />
              </button>
            ) : null}
          </div>

          <div className="pi-cars-gallery__footer">
            <p className="pi-cars-gallery__caption" title={caption}>
              <span>{caption}</span>
              {canNavigate ? (
                <em>
                  {index + 1}/{total}
                </em>
              ) : null}
            </p>

            {canNavigate ? (
              <div
                className="pi-cars-gallery__thumbs"
                ref={thumbsRef}
                role="listbox"
                aria-label="Photo thumbnails"
              >
                {slides.map((entry, thumbIndex) => {
                  const isActive = thumbIndex === index;
                  return (
                    <button
                      key={`${entry.url}-${thumbIndex}`}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      data-active={isActive ? "true" : "false"}
                      className={`pi-cars-gallery__thumb${
                        isActive ? " is-active" : ""
                      }`}
                      onClick={() => setIndex(thumbIndex)}
                      aria-label={`Show photo ${thumbIndex + 1}`}
                    >
                      <img
                        src={entry.url}
                        alt=""
                        loading="lazy"
                        onError={(event) => {
                          if (
                            event.currentTarget.src !== DEFAULT_VEHICLE_IMAGE
                          ) {
                            event.currentTarget.src = DEFAULT_VEHICLE_IMAGE;
                          }
                        }}
                      />
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function VehicleCard({
  item,
  index,
  onDetails,
  onGallery,
  onReport,
  reportAvailable,
}) {
  const resolvedImage = resolveVehicleImageUrl(item, DEFAULT_VEHICLE_IMAGE);
  const [imageSrc, setImageSrc] = useState(
    resolvedImage || DEFAULT_VEHICLE_IMAGE
  );
  const brand = cleanValue(item?.brands);
  const model = cleanValue(item?.model_name);
  const productName = displayValue(item?.product_name, "Untitled vehicle");
  const title = [brand, model].filter(Boolean).join(" ") || productName;
  const variant =
    model && productName && productName.toLowerCase() !== model.toLowerCase()
      ? productName
      : cleanValue(item?.product_category) ||
        bodyTypeLabel(item?.car_body_type) ||
        bodyTypeLabel(item?.body_type);
  const year = cleanValue(item?.year);
  const colour =
    cleanValue(item?.colour) ||
    cleanValue(item?.color) ||
    cleanValue(item?.exterior_colour) ||
    cleanValue(item?.exterior_color);
  const mileageLabel = cleanValue(item?.mileage)
    ? formatMileage(item.mileage)
    : null;
  const fuel = cleanValue(item?.fuel_type);
  const gearbox = transmissionLabel(item?.transmission);
  const descriptionFromApi = cleanValue(item?.product_description);
  const description =
    descriptionFromApi ||
    [
      [year, brand, model].filter(Boolean).join(" ") || productName,
      mileageLabel,
      fuel,
      gearbox,
    ]
      .filter(Boolean)
      .join(" · ");
  const specs = [
    year ? { key: "year", label: "Year", value: year, icon: "year" } : null,
    colour
      ? { key: "colour", label: "Colour", value: colour, icon: "colour" }
      : null,
    mileageLabel
      ? {
          key: "mileage",
          label: "Mileage",
          value: mileageLabel,
          icon: "mileage",
        }
      : null,
    fuel ? { key: "fuel", label: "Fuel", value: fuel, icon: "fuel" } : null,
    gearbox
      ? { key: "gearbox", label: "Gearbox", value: gearbox, icon: "gearbox" }
      : null,
  ].filter(Boolean);
  const priceMissing =
    !item?.poa &&
    !cleanValue(getBuyNowDisplayPrice(item, { buyNowFloor: true })) &&
    !cleanValue(item?.buy_now_price) &&
    !cleanValue(item?.product_price) &&
    !cleanValue(item?.price);
  const photoCount = Math.max(1, collectGallerySlides(item).length);

  useEffect(() => {
    setImageSrc(resolvedImage || DEFAULT_VEHICLE_IMAGE);
  }, [resolvedImage]);

  return (
    <article
      className="pi-cars__card"
      style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
    >
      <div className="pi-cars__card-media">
        <button
          type="button"
          className="pi-cars__card-media-btn"
          onClick={() => onDetails?.(item)}
          aria-label={`View details for ${title}`}
        >
          <img
            src={imageSrc}
            alt=""
            loading="lazy"
            onError={() => {
              if (imageSrc !== DEFAULT_VEHICLE_IMAGE) {
                setImageSrc(DEFAULT_VEHICLE_IMAGE);
              }
            }}
          />
        </button>

        <div className="pi-cars__card-overlay">
          <div className="pi-cars__card-overlay-icons">
            <button
              type="button"
              className="pi-cars__card-overlay-icon"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onGallery?.(item);
              }}
              tabIndex={-1}
              aria-label={`View ${photoCount} photo${
                photoCount === 1 ? "" : "s"
              }`}
            >
              <FiCamera size={16} aria-hidden />
              {photoCount > 1 ? <em>{photoCount}</em> : null}
            </button>
            {reportAvailable ? (
              <button
                type="button"
                className="pi-cars__card-overlay-icon"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onReport?.(item);
                }}
                tabIndex={-1}
                aria-label={`View report for ${title}`}
              >
                <FiFileText size={16} aria-hidden />
              </button>
            ) : null}
          </div>

          <div className="pi-cars__card-overlay-actions">
            <button
              type="button"
              className="pi-cars__card-overlay-details"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onDetails?.(item);
              }}
              tabIndex={-1}
            >
              Details
            </button>
            {reportAvailable ? (
            <button
              type="button"
              className="pi-cars__card-overlay-report"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onReport?.(item);
              }}
              tabIndex={-1}
            >
              View report
            </button>
          ) : null}
          </div>
        </div>

        <button
          type="button"
          className="pi-cars__card-camera"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onGallery?.(item);
          }}
          aria-label={`View ${photoCount} photo${photoCount === 1 ? "" : "s"}`}
        >
          <FiCamera size={14} aria-hidden />
          {photoCount > 1 ? <em>{photoCount}</em> : null}
        </button>
      </div>

      <div className="pi-cars__card-body">
        <div className="pi-cars__card-copy">
          {brand ? <p className="pi-cars__card-make">{brand}</p> : null}
          <h3 className="pi-cars__card-title">
            <button type="button" onClick={() => onDetails?.(item)}>
              {model || productName}
            </button>
          </h3>
          {variant && variant !== (model || productName) ? (
            <p className="pi-cars__card-variant">{variant}</p>
          ) : null}
          <p className="pi-cars__card-desc">{description}</p>
        </div>

        <div className="pi-cars__price-wrap">
          <div className="pi-cars__price-row">
            <div
              className={`pi-cars__price${priceMissing ? " is-missing" : ""}`}
            >
              <span>Cash Price</span>
              <strong>{formatPrice(item)}</strong>
            </div>
            <div className="pi-cars__price-divider" aria-hidden="true" />
            <p className="pi-cars__finance-note">
              Finance available
              <span>on enquiry</span>
            </p>
          </div>
        </div>

        {specs.length ? (
          <ul className="pi-cars__card-specs" aria-label="Vehicle highlights">
            {specs.map((spec) => (
              <li key={spec.key} className="pi-cars__card-spec">
                <span className="pi-cars__card-spec-icon" aria-hidden="true">
                  <CardSpecIcon type={spec.icon} />
                </span>
                <span className="pi-cars__card-spec-text">
                  <span className="pi-cars__card-spec-label">{spec.label}</span>
                  <span className="pi-cars__card-spec-value">{spec.value}</span>
                </span>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="pi-cars__card-actions">
          <button
            type="button"
            className="pi-cars__card-cta"
            onClick={() => onDetails?.(item)}
          >
            View details
            <span aria-hidden="true">→</span>
          </button>
          {reportAvailable ? (
            <button
              type="button"
              className="pi-cars__card-report"
              onClick={() => onReport?.(item)}
            >
              <FiFileText size={15} aria-hidden />
              View report
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function CardSpecIcon({ type }) {
  const common = {
    width: 16,
    height: 16,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.75,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  if (type === "year") {
    return (
      <svg {...common}>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M3 10h18M8 3v4M16 3v4" />
      </svg>
    );
  }
  if (type === "colour") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="8" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );
  }
  if (type === "mileage") {
    return (
      <svg {...common}>
        <path d="M4 19a8 8 0 0 1 16 0" />
        <path d="M12 19V11" />
        <path d="M12 11l4-2" />
      </svg>
    );
  }
  if (type === "fuel") {
    return (
      <svg {...common}>
        <path d="M7 20V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v14" />
        <path d="M5 20h14" />
        <path d="M15 10h2a2 2 0 0 1 2 2v3a1.5 1.5 0 0 0 3 0V9l-3-3" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M4.9 19.1 7 17M17 7l2.1-2.1" />
    </svg>
  );
}

function CarsPage({
  databuy,
  loading,
  brands,
  productDetail,
  detailLoading,
  getBuyNowList,
  getBrands,
  getProductDetail,
}) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortValue, setSortValue] = useState("1");
  const [brand, setBrand] = useState("");
  const [transmission, setTransmission] = useState("");
  const [mileage, setMileage] = useState("");
  const [selectedLot, setSelectedLot] = useState(null);
  const [galleryLot, setGalleryLot] = useState(null);
  const [reportLot, setReportLot] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [columnsPerRow, setColumnsPerRow] = useState(2);
  const [reportMap, setReportMap] = useState({});
  const reportCacheRef = useRef({});

  const lots = Array.isArray(databuy?.data) ? databuy.data : [];
  const total = Number(databuy?.total_object) || 0;
  const pageCount = Math.max(1, Number(databuy?.num_of_pages) || 1);
  const brandOptions = Array.isArray(brands) ? brands : [];
  const lotIdsKey = lots
    .map((item) => getProductId(item))
    .filter((id) => id != null && id !== "")
    .map(String)
    .join(",");

  const hasActiveFilters = Boolean(brand || transmission || mileage);
  const activeFilterCount = [brand, transmission, mileage].filter(Boolean)
    .length;

  const buildQuery = (nextPage = page) => {
    const params = {
      page: nextPage,
      page_size: PAGE_SIZE,
      sort_cars: sortValue,
    };
    if (brand) params.brands = brand;
    if (transmission) params.transmission = transmission;
    if (mileage) params.millage = mileage;
    return Object.keys(params)
      .map(
        (key) => `${key}=${encodeURIComponent(params[key] ?? "")}`
      )
      .join("&");
  };

  useEffect(() => {
    scrollTop();
    getBrands();
  }, []);

  useEffect(() => {
    getBuyNowList(buildQuery(page));
  }, [page, sortValue, brand, transmission, mileage]);

  useEffect(() => {
    let cancelled = false;
    const ids = lotIdsKey ? lotIdsKey.split(",") : [];
    if (!ids.length) return undefined;

    (async () => {
      const updates = {};
      await Promise.all(
        ids.map(async (id) => {
          const item = lots.find(
            (lot) => String(getProductId(lot)) === String(id)
          );
          if (hasReportHint(item)) {
            reportCacheRef.current[id] = true;
            updates[id] = true;
            return;
          }
          if (typeof reportCacheRef.current[id] === "boolean") {
            updates[id] = reportCacheRef.current[id];
            return;
          }
          const available = await checkReportAvailable(id);
          reportCacheRef.current[id] = available;
          updates[id] = available;
        })
      );
      if (!cancelled) {
        setReportMap((prev) => ({ ...prev, ...updates }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lotIdsKey]);

  const isReportAvailable = (item) => {
    const id = getProductId(item);
    if (id == null || id === "") return false;
    if (hasReportHint(item)) return true;
    return Boolean(reportMap[String(id)]);
  };

  const filtered = useMemo(
    () => lots.filter((item) => matchesSearch(item, search)),
    [lots, search]
  );

  const updateFilter = (setter) => (event) => {
    setter(event.target.value);
    setPage(1);
  };

  const clearFilters = () => {
    setBrand("");
    setTransmission("");
    setMileage("");
    setSearch("");
    setSortValue("1");
    setPage(1);
  };

  const goPage = (next) => {
    if (next < 1 || next > pageCount || next === page) return;
    setPage(next);
    scrollTop();
  };

  const openDetails = (item) => {
    const id = getProductId(item);
    setSelectedLot(item);
    if (id != null && id !== "") {
      getProductDetail(id);
    }
  };

  const closeDetails = () => {
    setSelectedLot(null);
  };

  const openGallery = (item) => {
    setGalleryLot(item);
  };

  const closeGallery = () => {
    setGalleryLot(null);
  };

  const openReport = (item) => {
    const id = getProductId(item);
    if (id == null || id === "") return;
    setReportLot(item);
  };

  const closeReport = () => {
    setReportLot(null);
  };

  return (
    <div className="pi-cars">
      <header className="pi-cars__top">
        <div className="pi-cars__top-bg" aria-hidden="true">
          <img
            src={`${publicUrl}/images/cars-hero.jpg`}
            alt=""
          />
          <span className="pi-cars__top-shade" />
        </div>
        <div className="pi-cars__top-inner">
          <p className="pi-cars__brand-mark">Sell Any Wheel</p>
          <h1>Used cars</h1>
          <p className="pi-cars__intro">
            Browse fixed-price vehicles ready for purchase. Search our range
            below — every listing includes clear cash pricing and key
            specification.
          </p>

          <label className="pi-cars__header-search">
            <FiSearch size={18} aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search used cars by make, model, or year"
              aria-label="Search used cars"
            />
            {search ? (
              <button
                type="button"
                className="pi-cars__header-search-clear"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <FiX size={16} />
              </button>
            ) : null}
          </label>
        </div>
      </header>

      <div className="pi-cars__shell">
        <button
          type="button"
          className="pi-cars__filters-toggle"
          onClick={() => setFiltersOpen(true)}
        >
          <FiSliders size={16} aria-hidden />
          Filters and sort
        </button>

        {filtersOpen ? (
          <button
            type="button"
            className="pi-cars__filters-backdrop"
            aria-label="Close filters"
            onClick={() => setFiltersOpen(false)}
          />
        ) : null}

        <aside
          className={`pi-cars__filters${filtersOpen ? " is-open" : ""}`}
          aria-label="Filters and sort"
        >
          <div className="pi-cars__filters-head">
            <h2>Filter results</h2>
            <button
              type="button"
              className="pi-cars__filters-close"
              onClick={() => setFiltersOpen(false)}
              aria-label="Close filters"
            >
              <FiX size={18} />
            </button>
          </div>

          <label className="pi-cars__filter">
            <span>Make</span>
            <select
              value={brand}
              onChange={updateFilter(setBrand)}
              aria-label="Filter by make"
            >
              <option value="">Any make</option>
              {brandOptions.map((item) => (
                <option
                  key={item?.id || item?.brand_name}
                  value={item?.brand_name}
                >
                  {item?.brand_name}
                </option>
              ))}
            </select>
          </label>

          <label className="pi-cars__filter">
            <span>Gearbox</span>
            <select
              value={transmission}
              onChange={updateFilter(setTransmission)}
              aria-label="Filter by gearbox"
            >
              {TRANSMISSION_OPTIONS.map((option) => (
                <option key={option.value || "any"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="pi-cars__filter">
            <span>Mileage</span>
            <select
              value={mileage}
              onChange={updateFilter(setMileage)}
              aria-label="Filter by mileage"
            >
              {MILEAGE_OPTIONS.map((option) => (
                <option key={option.value || "any"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <div className="pi-cars__filters-actions">
            {(hasActiveFilters || search || sortValue !== "1") && (
              <button
                type="button"
                className="pi-cars__filters-reset"
                onClick={clearFilters}
              >
                Reset filters
              </button>
            )}
            <button
              type="button"
              className="pi-cars__filters-apply"
              onClick={() => setFiltersOpen(false)}
            >
              View {loading && !lots.length ? "…" : filtered.length || total || 0}{" "}
              cars
            </button>
          </div>
        </aside>

        <main className="pi-cars__main">
          <div className="pi-cars__results-bar">
            <p className="pi-cars__count" aria-live="polite">
              {loading && !lots.length
                ? "Loading vehicles…"
                : `${filtered.length}${total ? ` of ${total}` : ""} results`}
            </p>

            <div className="pi-cars__results-tools">
              <div
                className="pi-cars__density"
                role="group"
                aria-label="Items per row"
              >
                <span>Per row</span>
                <button
                  type="button"
                  className={columnsPerRow === 2 ? "is-active" : ""}
                  aria-pressed={columnsPerRow === 2}
                  onClick={() => setColumnsPerRow(2)}
                >
                  2
                </button>
                <button
                  type="button"
                  className={columnsPerRow === 3 ? "is-active" : ""}
                  aria-pressed={columnsPerRow === 3}
                  onClick={() => setColumnsPerRow(3)}
                >
                  3
                </button>
              </div>

              <label className="pi-cars__sort">
                <span>Sort by</span>
                <select
                  value={sortValue}
                  onChange={updateFilter(setSortValue)}
                  aria-label="Sort cars"
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {loading && !lots.length ? (
            <div
              className={`pi-cars__grid pi-cars__grid--cols-${columnsPerRow}`}
              aria-busy="true"
            >
              {Array.from({ length: columnsPerRow === 3 ? 9 : 6 }).map(
                (_, i) => (
                  <div key={i} className="pi-cars__skeleton" />
                )
              )}
            </div>
          ) : !lots.length ? (
            <div className="pi-cars__empty">
              <strong>No vehicles match these filters</strong>
              <p>
                Try clearing filters or choosing a wider make, gearbox, or
                mileage range.
              </p>
              {(hasActiveFilters || sortValue !== "1") && (
                <button
                  type="button"
                  className="pi-cars__empty-link"
                  onClick={clearFilters}
                >
                  Clear filters
                  <FiArrowRight size={16} aria-hidden />
                </button>
              )}
            </div>
          ) : !filtered.length ? (
            <div className="pi-cars__empty">
              <strong>No matches for this search</strong>
              <p>Try another term, or clear the search field.</p>
            </div>
          ) : (
            <div
              className={`pi-cars__grid pi-cars__grid--cols-${columnsPerRow}`}
            >
              {filtered.map((item, index) => (
                <VehicleCard
                  key={item?.id || index}
                  item={item}
                  index={index}
                  onDetails={openDetails}
                  onGallery={openGallery}
                  onReport={openReport}
                  reportAvailable={isReportAvailable(item)}
                />
              ))}
            </div>
          )}

          {!loading && lots.length && pageCount > 1 ? (
            <nav className="pi-cars__pager" aria-label="Cars pagination">
              <button
                type="button"
                className="pi-cars__pager-btn"
                onClick={() => goPage(page - 1)}
                disabled={page <= 1}
              >
                <FiChevronLeft size={18} aria-hidden />
                Previous
              </button>
              <span className="pi-cars__pager-status">
                Page {page} of {pageCount}
              </span>
              <button
                type="button"
                className="pi-cars__pager-btn"
                onClick={() => goPage(page + 1)}
                disabled={page >= pageCount}
              >
                Next
                <FiChevronRight size={18} aria-hidden />
              </button>
            </nav>
          ) : null}
        </main>
      </div>

      <VehicleDetailModal
        open={Boolean(selectedLot)}
        listItem={selectedLot}
        product={productDetail}
        loading={detailLoading}
        onClose={closeDetails}
        reportAvailable={isReportAvailable(selectedLot)}
        onReport={openReport}
      />
      <VehicleGalleryModal
        open={Boolean(galleryLot)}
        item={galleryLot}
        onClose={closeGallery}
      />
      <VehicleReportModal
        open={Boolean(reportLot)}
        item={reportLot}
        onClose={closeReport}
      />
    </div>
  );
}

const mapStateToProps = (state) => ({
  databuy: state.product.databuy,
  loading: Boolean(state.product.fetchingList),
  brands: state.sellMyCar?.brands,
  productDetail: state.product.current,
  detailLoading: Boolean(state.product.fetchingCurrent),
});

const mapDispatchToProps = (dispatch) => ({
  getBuyNowList: (query) =>
    dispatch({
      type: PRODUCT_LIST_BUY,
      payload: {
        type: PRODUCT_LIST_BUY,
        method: GET,
        label: query ? `${Labels.buynow_list}?${query}` : Labels.buynow_list,
        hideOverlayLoader: true,
      },
    }),
  getProductDetail: (code) =>
    dispatch({
      type: PRODUCT_DETAIL,
      payload: {
        type: PRODUCT_DETAIL,
        method: GET,
        label: `${Labels.product_detail}${code}/`,
        hideOverlayLoader: true,
      },
    }),
  getBrands: () =>
    dispatch({
      type: BRANDS,
      payload: {
        type: BRANDS,
        method: POST,
        label: Labels.brands,
        hideOverlayLoader: true,
      },
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(CarsPage);
